import {type SafeParseReturnType, ZodType} from 'zod';
import jsonpath from 'jsonpath';
import {makeLogger} from '../../../logger/logger';
import {type Callbacks, makeObservableProxy, type ObservableChange} from './fluxProxy';
import {StencilSubscription} from './fluxStencilIntegration';


const {
  debug,
  // info,
  warn,
  // err
} = makeLogger({
  pathToCurrentFile: 'utils/libs/dataflux.ts',
});

/**
 * Represents a callback function that is invoked by Dataflux when a subscribed property value is received.
 * @template T - The type of the value being received.
 * @callback SubscriberCallback
 * @param {string} property - The name of the property that received the value.
 * @param {T} value - The value that was received.
 * @returns {void} - No return value.
 */
type SubscriberCallback<T> = (property: string, value: T) => void;

type SubscriberTypeValidationFailedCallback<In, Out> = (
  key: string,
  value: unknown,
  status: SafeParseReturnType<In, Out>
) => void;

interface Subscriber<T> {
  id: symbol;
  jsonPath: string;
  callback: SubscriberCallback<T>;
  schema: ZodType<T>;
  validationErrorCallback?: SubscriberTypeValidationFailedCallback<unknown, T>;
}

/**
 * The `SubscriberUnsafe` interface represents a subscriber that is used for unsafe subscription.
 *
 * @interface
 * @template T - The type of data that the subscriber will receive.
 */
interface SubscriberUnsafe<T> {
  id: symbol;
  jsonPath: string;
  callback: SubscriberCallback<T>;
}

type Unsubscribe = () => void;

export interface Action<InType, AssignedType> {
  type: string; // Identifier for the action
  run: (currentVal: AssignedType, payload: InType) => AssignedType; // Function to process and produce new data
}

export type LowerBoundedType<Base, T extends Base = Base> = T;


export const getMatchingSubscribers = <T>(
  dataflux: DataFlux<T>,
  pathThatWIllChange,
  subscribers: Subscriber<T>[] | SubscriberUnsafe<T>[],
)
  : Subscriber<T>[] | SubscriberUnsafe<T>[] => {

    // debug('Subscriber list', subscribers);

    if (subscribers.length == 0)
      return subscribers;


    const activeSubscribers = subscribers.filter(subscriber => {

        const subscriberPaths = jsonpath
          .nodes(dataflux.root, subscriber.jsonPath)
          .map(node => jsonpath.stringify(node.path))
          .filter(subscriberPath => {
              //subscriberPath ex $.foo
              //pathThatWIllChange ex $.foo.bar
              // => subscriber should be notified
              return pathThatWIllChange.startsWith(subscriberPath);
          });
        return subscriberPaths.length > 0;
    });
    // debug('Active subscribers', activeSubscribers);
    return activeSubscribers;
};




/**
 * Adds a subscriber for a specific JSON path.
 *
 * @template T - The type of the value being subscribed to.
 * @param json - the JSON-like object to be observed.
 *
 * @example
 * const data = { firstName: 'John', lastName: 'Doe' };
 * const flux = DataFlux(data);
 * const key = 'firstName'; //valid JsonPath key
 * const unsubscribe = flux.subscribe(key, (jsonPathKey, newValue) => {
 *   console.log(`${jsonPathKey} changed to ${newValue}`);
 * }, z.string());
 *
 * flux.root.firstName = 'Jane'; // logs "firstName changed to Jane"
 *
 * unsubscribe();
 */
export default class DataFlux<T extends {}> {
  private __isDataFlux = true;
  private subscribers: Subscriber<unknown>[] = [];
  private subscribersUnsafe: SubscriberUnsafe<unknown>[] = [];

  private raw: T;

  private _root: T;
  get root(){
      return this._root;
  }

  constructor(input: T, callback?: Callbacks) {

      this.raw = input;

      const ss = StencilSubscription();

      this._root = makeObservableProxy(input, {
        onGet: [
          ...callback?.onGet ?? [],
          ...ss.onGet,
        ],
        onAdd: [
          ...callback?.onAdd ?? [],
          (c) => this.notifySubscribers(c),
          ...ss.onAdd,
        ],
        onUpdate: [
          ...callback?.onUpdate ?? [],
          (c) => this.notifySubscribers(c),
          ...ss.onUpdate,
        ],
        onDelete: [
          ...callback?.onDelete ?? [],
          ...ss.onDelete,
        ],
      });
  }



  /**
   * Adds a subscriber for a specific JSON path.
   * @template T - The type of the value being subscribed to.
   * @param jsonPath - The JSON path to subscribe to.
   * @param callback - The callback function to be executed when the subscribed value changes.
   * @param schema - The Zod Schema definition for the subscribed value. Callback will not be executed when schema doesn't match
   * @param validationErrorCallback - callback to be called when data doesn't match schema
   * @param initialExecution - Should the callback be executed immediately after subscription? Default is true.
   * @returns unsubscribe - The unsubscribe function.
   * Simple usage:
   * ```typescript
   * flux.subscribe('$.b.c', (propertyKey, newValue) => {
   *  console.log(`${propertyKey} changed to ${newValue}`);
   *  }, z.string());
   * ```
   * _Where the subscription code should be called?_
   * Subscription in Stencil components must happen in connectedCallback(),
   * together with the unsubscribe call in disconnectedCallback().
   * This way, when component isn't present - it won't be updated, decreasing the load on the system.
   * Reference: https://stenciljs.com/docs/reactive-data
   *
   * _Triggering rerender_
   * If you update a member field that's not marked as @State/@Prop, it won't trigger a re-render unless you call forceUpdate(this):
   * ```
   * import { Component, forceUpdate, h } from '@stencil/core';
   *
   * @Watch('aria-label') //updating a non@Prop non@State field doesn't trigger rerender too!
   * onAriaLabelChange() {
   *   forceUpdate(this); // Forces a re-render
   * }
   * ```
   */
  public subscribe<S>(
    jsonPath: string,
    callback: SubscriberCallback<S>,
    schema: ZodType<S>,
    validationErrorCallback?: SubscriberTypeValidationFailedCallback<unknown, S>,
    initialExecution: boolean = true
  ): Unsubscribe {
      const id = Symbol(); //new uniq symbol for each call
      this.subscribers.push({id, jsonPath, callback, schema, validationErrorCallback});

      if (initialExecution){
        debug('Initial execution for subscribers');
        callback(jsonPath, jsonpath.query(this.root, jsonPath)[0]);
      }

      // Return an unsubscribe function
      return (): void => {
          this.subscribers = this.subscribers.filter(sub => sub.id !== id);
      };
  };


  /**
   * Subscribes a callback function to receive updates for a specific JSON path.
   *
   * @param {string} jsonPath - The JSON path to subscribe to.
   * @param {SubscriberCallback<S>} callback - The callback function to handle updates.
   * @param initialExecution - Should the callback be executed immediately after subscription? Default is true.
   * @returns {Unsubscribe} - The unsubscribe function to remove the subscription.
   */
  public subscribeUnsafe<S>(
    jsonPath: string,
    callback: SubscriberCallback<S>,
    initialExecution: boolean = true
  ): Unsubscribe {
      const id = Symbol(); //new uniq symbol for each call
      this.subscribersUnsafe.push({id, jsonPath, callback});

      if (initialExecution)
        callback(jsonPath, jsonpath.query(this.root, jsonPath)[0]);

      // Return an unsubscribe function
      return (): void => {
          this.subscribersUnsafe = this.subscribersUnsafe.filter(sub => sub.id !== id);
      };
  };

  private executeSubscriber(subscriber, value, jsonPath) {
      const valueChecked = subscriber.schema.safeParse(value);
      try {
        if (valueChecked.success) {
          subscriber.callback(jsonPath, value); //original data
          // subscriber.callback(jsonPath, valueChecked.data); //data wrapped by zod

        } else if (subscriber.validationErrorCallback) {
          subscriber.validationErrorCallback(jsonPath, value, valueChecked);
        } //if no validation error callback, we just ignore the value
      } catch (e) {
        warn('Subscriber unsafe call failed', {e, value, valueChecked, subscriber});
      }
  }

  private executeSubscriberUnsafe(subscriber, value, jsonPath) {
      try {
        subscriber.callback(jsonPath, value);
      } catch (e) {
        warn('Subscriber unsafe call failed', e, subscriber);
      }
  }

  private notifySubscribers(change: ObservableChange) {

      const {
        // type,
        // target,
        // property,
        newValue,
        // jsonPointer,
      } = change;
      const currentPath = change.currentPath;
      // debug(`Notifying for ${type}`, currentPath, newValue);

      const subscribers = getMatchingSubscribers(this, currentPath, this.subscribers);
      const subscribersUnsafe = getMatchingSubscribers(this, currentPath, this.subscribersUnsafe);

      /*
    debug('Evaluated subscribers:', {
      root: this.root,
      currentPath,
      jsonPointer,
      target,
      newValue,
      property,
      subscribers,
      subscribersUnsafe,
    });
    //*/

      subscribers.forEach(subscriber => {
          debug('Change x Subscriber', {
            subscriber, currentPath, newValue
          });
          this.executeSubscriber(subscriber, newValue, currentPath);
      });
      subscribersUnsafe.forEach(subscriber => {
          debug('Change x Subscriber', {
            subscriber, currentPath, newValue
          });
          this.executeSubscriberUnsafe(subscriber, newValue, currentPath);
      });
  };


  /**
   * Dispatches an action on the specified entity path.
   *
   * @template InType - The type of the entity
   * @template AssignedType - The type of the payload
   * @param {string} entityPath - The path to the entity in the JSON object
   * @param {Action<InType,AssignedType>} action - The action to be performed on the entity, returning value of type <AssignedType>
   * @param payload - parameter for the action to be called with
   * @return {AssignedType} - The new value of the entity, calculated from payload by executing the action
   *
   * ```typescript
   * const incrementAction: Action<number,number> = {
   *   type: 'INCREMENT',
   *   payload: 1,
   *   run: (currentVal: number, payload:number) => currentVal + payload
   * };
   *
   * //import incrementAction from 'actions';
   * dispatch('$.a', incrementAction);
   * ```
   */
  public dispatch<InType, AssignedType>(entityPath: string, action: Action<InType, AssignedType>, payload: InType) {
      const currentValue = jsonpath.query(this.root, entityPath)[0];
      const newValue = action.run(currentValue, payload);
      return jsonpath.value(this.root, entityPath, newValue);
  };

  static unsubscribeAll(calls: Unsubscribe | Unsubscribe[] | null): void {
      if (Array.isArray(calls)) {
        calls.forEach(call => call());
      } else if (calls instanceof Function) {
        calls();
      }
  }
}


// TODO: napisać jak webkomponent ma robić DataFlux, jak go przekazywać w dół drzewa
// TODO: nauczyć się ZOD :-)
